import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { StatCard, Card, Badge } from "@/components/kit";
import { money, shortDate } from "@/lib/api";
import { TrendUp, Warning, Wallet, Users, HourglassMedium, HandCoins, PiggyBank } from "@phosphor-icons/react";

export default function DashboardPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [collections, setCollections] = useState([]);
  const [maturing, setMaturing] = useState([]);

  useEffect(() => {
    (async () => {
      const [s, c, m] = await Promise.all([
        api.get("/dashboard/summary"),
        api.get("/collections?limit=8"),
        api.get("/maturity/list?days=30").catch(() => ({ data: [] })),
      ]);
      setData(s.data);
      setCollections(c.data);
      setMaturing(m.data);
    })().catch(() => {});
  }, []);

  if (user?.role === "agent") {
    return <AgentDashboard user={user} data={data} collections={collections} />;
  }

  return (
    <div className="w-full p-4 md:p-8 pb-24 md:pb-8 fade-up" data-testid="dashboard-page">
      <div className="mb-6">
        <div className="text-sm text-slate-500">Welcome back,</div>
        <h1 className="heading text-2xl md:text-3xl font-semibold text-slate-900">{user?.name}</h1>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          label="Customers"
          value={data?.total_customers ?? "—"}
          testId="stat-customers"
        />
        <StatCard
          label="Total Savings"
          value={data ? money(data.total_savings) : "—"}
          tone="positive"
          testId="stat-savings"
        />
        <StatCard
          label="Loan Outstanding"
          value={data ? money(data.total_outstanding) : "—"}
          testId="stat-outstanding"
        />
        <StatCard
          label="Overdue Installments"
          value={data?.overdue_installments ?? "—"}
          tone={data?.overdue_installments > 0 ? "negative" : "default"}
          hint={`${data?.overdue_loans ?? 0} loans affected`}
          testId="stat-overdue"
        />
        <Link to="/savings/maturity" data-testid="stat-maturing-link" className="block min-w-0">
          <div
            className={`bg-white border rounded-xl shadow-sm p-5 h-full transition-shadow min-w-0 ${
              (data?.maturing_soon_count || data?.matured_pending_count)
                ? "border-amber-300 blink-alert"
                : "border-slate-200"
            }`}
          >
            <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold mb-3 truncate">
              Deposit Maturity
            </div>
            <div className="heading text-xl md:text-2xl font-semibold text-amber-700 flex items-center gap-2 truncate" data-testid="stat-maturing">
              {(data?.maturing_soon_count ?? 0) + (data?.matured_pending_count ?? 0)}
              {(data?.matured_pending_count || 0) > 0 && (
                <span className="text-amber-600 text-lg"><span className="blink-dot" /></span>
              )}
            </div>
            <div className="text-xs text-slate-500 mt-2 truncate">
              {(data?.matured_pending_count || 0) > 0
                ? `${data.matured_pending_count} matured · ${data?.maturing_soon_count || 0} upcoming`
                : "RD / FD in next 30 days"}
            </div>
          </div>
        </Link>
        <Link to="/loans/closure" data-testid="stat-loan-closure-link" className="block min-w-0">
          <div
            className={`bg-white border rounded-xl shadow-sm p-5 h-full transition-shadow min-w-0 ${
              (data?.loan_closure_alerts || 0) > 0 ? "border-rose-300 blink-alert" : "border-slate-200"
            }`}
          >
            <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold mb-3 truncate">
              Loan Closure
            </div>
            <div className="heading text-xl md:text-2xl font-semibold text-rose-700 flex items-center gap-2 truncate" data-testid="stat-loan-closure">
              {data?.loan_closure_alerts ?? "—"}
              {(data?.loan_closure_alerts || 0) > 0 && (
                <span className="text-rose-600 text-lg"><span className="blink-dot" /></span>
              )}
            </div>
            <div className="text-xs text-slate-500 mt-2 truncate">Nearing or past last EMI</div>
          </div>
        </Link>
        <StatCard
          label="Expenses (MTD)"
          value={data ? money(data.mtd_expenses_total || 0) : "—"}
          tone="warn"
          testId="stat-expenses-mtd"
        />
      </div>

      {maturing.length > 0 && (
        <Card className="p-5 mb-6 border-amber-200 bg-amber-50/30" data-testid="maturing-widget">
          <div className="flex items-center justify-between mb-3">
            <h3 className="heading font-semibold inline-flex items-center gap-2">
              <HourglassMedium size={18} className="text-amber-700" /> Maturing in next 30 days
            </h3>
            <Badge tone="warning">{maturing.length} account{maturing.length === 1 ? "" : "s"}</Badge>
          </div>
          <div className="divide-y divide-amber-100">
            {maturing.slice(0, 6).map((m) => (
              <Link
                to={`/savings/${m.id}`}
                key={m.id}
                data-testid={`maturing-row-${m.id}`}
                className="py-2 flex items-center justify-between hover:bg-amber-100/40 rounded-md px-2 -mx-2"
              >
                <div>
                  <div className="text-sm font-medium text-slate-900">
                    {m.customer_name || "Customer"} <span className="text-xs uppercase text-slate-500 ml-1">{m.product}</span>
                  </div>
                  <div className="text-xs text-slate-500 mono">
                    Maturity {shortDate(m.maturity_date)} · {m.customer_phone || ""}
                  </div>
                </div>
                <div className="text-right">
                  <div className="mono text-amber-800 font-medium text-sm">
                    {money(m.maturity_value || m.balance || 0)}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="heading text-lg font-semibold">Today&apos;s Collections</h3>
            <Badge tone="success">{data?.today_collections_count ?? 0} entries</Badge>
          </div>
          <div className="text-3xl heading font-semibold text-emerald-700 mb-4 mono">
            {data ? money(data.today_collections_total) : "—"}
          </div>
          <div className="divide-y divide-slate-100">
            {collections.length === 0 && (
              <div className="text-sm text-slate-500 py-6 text-center">No collections yet.</div>
            )}
            {collections.map((c) => (
              <div key={c.id} className="py-3 flex items-center justify-between text-sm">
                <div>
                  <div className="font-medium text-slate-900 mono">{c.receipt_no}</div>
                  <div className="text-xs text-slate-500">
                    {c.kind === "savings_deposit" ? "Savings deposit" : "Loan repayment"} · {new Date(c.created_at).toLocaleString()}
                  </div>
                </div>
                <div className="mono font-medium text-emerald-700">{money(c.amount)}</div>
              </div>
            ))}
          </div>
        </Card>

        <div className="space-y-6">
          <Card className="p-6">
            <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold mb-4">
              Savings by Product
            </div>
            <div className="space-y-3">
              {(data?.savings_by_product || []).map((s) => (
                <div key={s._id} className="flex items-center justify-between text-sm">
                  <span className="capitalize font-medium">{productName(s._id)}</span>
                  <span className="mono text-slate-900">{money(s.total)}</span>
                </div>
              ))}
              {(!data?.savings_by_product || data.savings_by_product.length === 0) && (
                <div className="text-xs text-slate-500">No savings accounts yet.</div>
              )}
            </div>
          </Card>

          <Card className="p-6">
            <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold mb-4">
              Loans by Type
            </div>
            <div className="space-y-3">
              {(data?.loans_by_type || []).map((s) => (
                <div key={s._id} className="flex items-center justify-between text-sm">
                  <span className="capitalize font-medium">{s._id}-backed</span>
                  <span className="mono text-slate-900">{money(s.outstanding)}</span>
                </div>
              ))}
              {(!data?.loans_by_type || data.loans_by_type.length === 0) && (
                <div className="text-xs text-slate-500">No loans yet.</div>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

function productName(k) {
  return { daily: "Daily Savings", rd: "Recurring Deposit", fd: "Fixed Deposit" }[k] || k;
}

function AgentTile({ label, value, tone = "default", testId, hint, icon: Icon }) {
  const toneClasses = {
    default: "text-slate-900",
    positive: "text-emerald-700",
    negative: "text-rose-700",
    warn: "text-amber-700",
    info: "text-sky-700",
  };
  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-4" data-testid={testId}>
      <div className="flex items-center gap-2 mb-2">
        {Icon && <Icon size={16} className="text-slate-400" weight="regular" />}
        <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">{label}</div>
      </div>
      <div className={`heading text-xl font-semibold mono ${toneClasses[tone]}`}>{value}</div>
      {hint && <div className="text-[11px] text-slate-500 mt-1">{hint}</div>}
    </div>
  );
}

function AgentDashboard({ user, data, collections }) {
  return (
    <div className="p-4 pb-28 fade-up" data-testid="agent-dashboard">
      <div className="mb-5">
        <div className="text-xs text-slate-500">Namaste,</div>
        <h1 className="heading text-xl font-semibold text-slate-900">{user?.name}</h1>
        <div className="text-[11px] text-slate-500 mt-0.5">Field Agent · Your customers only</div>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <AgentTile
          label="Customers"
          value={data?.total_customers ?? "—"}
          icon={Users}
          testId="agent-stat-customers"
        />
        <AgentTile
          label="Today's Collection"
          value={data ? money(data.today_collections_total) : "—"}
          tone="positive"
          hint={`${data?.today_collections_count ?? 0} entries`}
          icon={Wallet}
          testId="agent-stat-today"
        />
        <AgentTile
          label="Total Savings"
          value={data ? money(data.total_savings) : "—"}
          tone="positive"
          icon={PiggyBank}
          testId="agent-stat-savings"
        />
        <AgentTile
          label="Total Loan"
          value={data ? money(data.total_loan_disbursed || 0) : "—"}
          icon={HandCoins}
          testId="agent-stat-loan-disbursed"
        />
        <AgentTile
          label="Loan Outstanding"
          value={data ? money(data.total_outstanding) : "—"}
          tone="warn"
          icon={HandCoins}
          testId="agent-stat-outstanding"
        />
        <AgentTile
          label="Overdue"
          value={data?.overdue_installments ?? "—"}
          tone={data?.overdue_installments > 0 ? "negative" : "default"}
          hint={`${data?.overdue_loans ?? 0} loans affected`}
          icon={Warning}
          testId="agent-stat-overdue"
        />
      </div>

      <Link
        to="/loan-requests"
        data-testid="agent-cta-raise-loan"
        className="block w-full text-center bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white font-medium rounded-2xl py-3.5 shadow-sm mb-5 transition-colors"
      >
        + Raise Loan Request
      </Link>

      <Card className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="heading text-sm font-semibold text-slate-800">Today&apos;s Collections</h3>
          <Badge tone="success">{collections?.length ?? 0}</Badge>
        </div>
        <div className="divide-y divide-slate-100">
          {(!collections || collections.length === 0) && (
            <div className="text-xs text-slate-500 py-4 text-center">No collections logged yet.</div>
          )}
          {collections?.map((c) => (
            <div key={c.id} className="py-2.5 flex items-center justify-between text-sm">
              <div className="min-w-0">
                <div className="font-medium text-slate-900 mono truncate">{c.receipt_no}</div>
                <div className="text-[11px] text-slate-500 truncate">
                  {c.kind === "savings_deposit" ? "Savings" : "Loan"} · {new Date(c.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </div>
              </div>
              <div className="mono font-semibold text-emerald-700 shrink-0">{money(c.amount)}</div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
